import { createPortal } from "react-dom";
import {
  ArrowRight,
  CheckCircle2,
  Lock,
  Sparkles,
  X,
} from "lucide-react";

const featureRows = [
  {
    title: "Enhanced AI Assistance",
    subtitle: "Get smarter, faster answers from FINU AI.",
  },
  {
    title: "Smarter Financial Insights",
    subtitle: "Personalized insights to grow your wealth.",
  },
  {
    title: "Shopping Assistant",
    subtitle: "Find the best deals and save more.",
  },
];

const PwaInstallSuccessModal = ({ open, onClose, onGoToAskFinu }) => {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-hidden overscroll-none">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 bg-black/50"
      />

      <div className="pointer-events-none fixed inset-0 flex items-center justify-center px-4 py-4">
        <div className="pointer-events-auto w-full max-w-[340px] overflow-hidden rounded-[22px] border border-white/70 bg-white shadow-[0_24px_72px_rgba(15,23,42,0.34)] sm:max-w-[380px]">
          <div className="max-h-[70dvh] overflow-y-auto px-3 pb-3 pt-3 sm:max-h-[calc(100dvh-2rem)] sm:px-5 sm:pb-5 sm:pt-5">
            <div className="relative flex flex-col items-center">
              <button
                type="button"
                onClick={onClose}
                className="absolute right-0 top-0 flex size-8 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-[#667085] shadow-[0_4px_12px_rgba(15,23,42,0.05)] transition-colors hover:text-[#344054]"
                aria-label="Close"
              >
                <X className="h-[16px] w-[16px]" strokeWidth={2.2} />
              </button>

              <div className="relative mt-1 flex h-16 w-16 items-center justify-center rounded-full bg-[#EEF4FF] shadow-[0_0_0_4px_rgba(45,91,255,0.08)] sm:mt-1.5 sm:h-20 sm:w-20 sm:shadow-[0_0_0_6px_rgba(45,91,255,0.08)]">
                <div className="pointer-events-none absolute -left-1.5 top-1/4 size-0.5 rounded-full bg-[#2D5BFF]" />
                <div className="pointer-events-none absolute -right-1 top-2/3 size-0.5 rounded-full bg-[#6F7DFF]" />
                <div className="pointer-events-none absolute left-1 top-2 size-1 rotate-45 rounded-[2px] bg-[#7E5BFF]" />
                <div className="pointer-events-none absolute right-1.5 top-2 size-1 rounded-full bg-[#2D5BFF]/80" />
                <div className="pointer-events-none absolute -left-2 top-5 size-1 rounded-full bg-[#7E5BFF]/70" />
                <div className="pointer-events-none absolute -right-2 top-5.5 size-1.5 rotate-45 rounded-[2px] bg-[#2D5BFF]/70" />
                <div className="pointer-events-none absolute bottom-2 left-2 size-0.5 rounded-full bg-[#8AA0FF]/70" />
                <Sparkles
                  className="size-5 text-[#2D5BFF] sm:size-7"
                  strokeWidth={2}
                />
              </div>

              <h3 className="mt-3 text-center text-[18px] font-extrabold leading-[1.02] tracking-[-0.04em] text-[#0F172A] sm:mt-4 sm:text-[24px] sm:leading-[1.03]">
                <span className="block">Welcome to</span>
                <span className="block bg-gradient-to-r from-[#1F4FD8] to-[#2D5BFF] bg-clip-text text-transparent">
                  FINU Plus
                </span>
              </h3>

              <p className="mt-2 max-w-[23rem] text-center text-[12px] leading-[1.28] text-[#475467] sm:mt-2.5 sm:text-[14px] sm:leading-[1.35]">
                Your free plan upgrade has been
                <span className="block">activated successfully.</span>
              </p>

              <div className="mt-3 w-full sm:mt-4">
                <div className="rounded-[16px] bg-white px-2.5 py-0.5 sm:rounded-[18px] sm:px-4">
                  {featureRows.map(({ title, subtitle }) => (
                    <div
                      key={title}
                      className="flex items-start gap-2.5 border-b border-[#E4E7EC] py-2.5 last:border-b-0 sm:gap-3 sm:py-3.5"
                    >
                      <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-xl bg-[#F4F7FF] text-[#2D5BFF] sm:size-9">
                        <CheckCircle2 className="h-[16px] w-[16px] sm:h-[18px] sm:w-[18px]" strokeWidth={2.1} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold leading-[1.2] text-[#101828] sm:text-[13px] sm:leading-5">
                          {title}
                        </p>
                        <p className="mt-0.5 text-[10px] leading-[1.25] text-[#667085] sm:text-[12px] sm:leading-4">
                          {subtitle}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={onGoToAskFinu}
                className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-[14px] bg-[linear-gradient(90deg,#1F4FD8_0%,#2D5BFF_100%)] px-5 text-[13px] font-semibold text-white shadow-none transition-opacity hover:opacity-95 sm:mt-4 sm:h-12 sm:px-6 sm:text-[15px]"
              >
                <Sparkles className="size-3.5" strokeWidth={2.2} />
                <span>Start Exploring</span>
                <ArrowRight className="size-3.5" strokeWidth={2.2} />
              </button>

              <div className="mt-2.5 flex items-center gap-1.5 text-[10px] text-[#667085] sm:mt-3 sm:text-[12px]">
                <Lock className="h-[12px] w-[12px]" strokeWidth={2.1} />
                <span>Secure. Private. Always in your control.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PwaInstallSuccessModal;
